/**
 * Microsoft Clarity — thin, failure-tolerant wrapper.
 *
 * The tag itself is loaded in public/index.html. Everything here is optional
 * enrichment on top of it: Clarity already records sessions, builds heatmaps and
 * reports exit pages without a single call from application code. These helpers
 * exist so a recording can be *filtered* — by role, by signup step, by whether
 * the visitor was logged in — which is what turns "someone dropped off" into
 * "sellers drop off on OTP".
 *
 * Two rules this file enforces:
 *
 * 1. Never throw. Clarity is blocked by many ad blockers and by corporate DNS,
 *    so `window.clarity` is frequently undefined. Analytics must never be able
 *    to break a checkout, so every call is guarded and swallowed.
 *
 * 2. Never send personal data. `identify` receives the Mongo id only — never an
 *    email, phone, or name. Those are the fields Clarity would store on Microsoft
 *    servers and surface in its UI, and nobody consented to that. The id is
 *    enough to line a recording up with an order when support needs to.
 */

const isEnabled = () => typeof window !== 'undefined' && typeof window.clarity === 'function';

const call = (...args) => {
  if (!isEnabled()) return false;
  try {
    window.clarity(...args);
    return true;
  } catch (error) {
    // Deliberately quiet. A failed analytics call is not worth a console error
    // on a buyer's screen, and never worth interrupting the flow it sits in.
    return false;
  }
};

/**
 * Name a thing that happened. Shows up in Clarity as a filter and can be used
 * as a funnel step.
 */
export const trackEvent = (name) => {
  if (!name) return false;
  return call('event', name);
};

/**
 * Attach a key/value to the session so recordings can be filtered by it.
 * Values are coerced to string because Clarity drops non-strings silently.
 */
export const setTag = (key, value) => {
  if (!key || value === undefined || value === null || value === '') return false;
  return call('set', key, String(value));
};

/**
 * Tie the session to an account.
 *
 * `customId` must be an opaque identifier — the Mongo _id. Passing an email here
 * would put customer PII into a third-party analytics tool, so callers that only
 * have an email should pass nothing.
 */
export const identify = (customId, { role } = {}) => {
  if (!customId) return false;
  const ok = call('identify', String(customId));
  if (role) setTag('role', role);
  return ok;
};

/** Login succeeded. `role` is 'buyer' | 'seller' | 'delivery' | 'admin'. */
export const trackLogin = (role, userId) => {
  trackEvent(`login_${role}`);
  setTag('role', role);
  setTag('auth_state', 'logged_in');
  if (userId) identify(userId, { role });
};

/** Registration completed — the account now exists. */
export const trackSignup = (role, userId) => {
  trackEvent(`signup_${role}`);
  setTag('role', role);
  setTag('signup_completed', 'yes');
  if (userId) identify(userId, { role });
};

/**
 * A step inside a multi-step signup, so the funnel shows where people abandon.
 * e.g. trackSignupStep('buyer', 'otp_sent').
 */
export const trackSignupStep = (role, step) => {
  trackEvent(`signup_${role}_${step}`);
};

/** Logged out deliberately — distinct from simply leaving the site. */
export const trackLogout = (role) => {
  trackEvent(`logout_${role}`);
  setTag('auth_state', 'logged_out');
};

// ─── Commerce funnels ────────────────────────────────────────────────────────
//
// Each of these is paired: something starts, then it either completes or it
// does not. The gap between the two is the number worth looking at, and the
// recordings behind that gap are where the reason lives.

/** Virtual try-on. `outcome`: 'opened' | 'started' | 'success' | 'failed'. */
export const trackTryOn = (outcome, productId) => {
  trackEvent(`tryon_${outcome}`);
  // Only the id — a product id is not personal data, unlike anything on the
  // buyer. Lets a recording be matched back to the exact product.
  if (productId) setTag('tryon_product', productId);
};

/** Add to cart. `outcome`: 'success' | 'failed' | 'blocked_auth'. */
export const trackAddToCart = (outcome, productId) => {
  trackEvent(`add_to_cart_${outcome}`);
  if (productId) setTag('cart_product', productId);
};

/**
 * Payment. `outcome`: 'started' | 'success' | 'failed' | 'cancelled'
 * | 'recovered_cod' | 'back_to_cart'.
 *
 * 'cancelled' means the buyer closed the gateway themselves; 'failed' means it
 * broke. Clarity cannot tell those apart on its own and they mean very
 * different things, so they are separate events.
 */
export const trackPayment = (outcome, method) => {
  trackEvent(`payment_${outcome}`);
  if (method) setTag('payment_method', method);
};

/** Order cancellation. `outcome`: 'opened' | 'confirmed' | 'kept'. */
export const trackOrderCancel = (outcome, reason) => {
  trackEvent(`order_cancel_${outcome}`);
  // The reason comes from a fixed dropdown, so it is safe to tag — it is never
  // free text a buyer could put their own details into.
  if (outcome === 'confirmed' && reason) setTag('cancel_reason', reason);
};

const clarity = {
  isEnabled,
  trackEvent,
  setTag,
  identify,
  trackLogin,
  trackSignup,
  trackSignupStep,
  trackLogout,
  trackTryOn,
  trackAddToCart,
  trackPayment,
  trackOrderCancel
};

export default clarity;
